import { useState } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db8bq5rk13ra8mig8pag";

const cooperationTypes = ["内容共创", "品牌传播", "活动联合", "媒体合作", "其他"];

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const decoy = String(formData.get("website_alt") || "").trim();
    if (decoy) {
      setStatus("success");
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setMessageLength(0);
      } else {
        const lower = String(serverMsg).toLowerCase();
        setFormError(
          lower.includes("spam")
            ? "提交未通过校验，请检查填写内容后重试。"
            : serverMsg || "提交失败，请稍后重试。"
        );
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后重试。");
      setStatus("error");
    }
  };

  return (
    <div id="contact-form" className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-8">
      <h3 className="font-heading font-semibold text-xl text-foreground-950">
        咨询与合作联系
      </h3>
      <p className="mt-2 text-sm text-foreground-500">
        告诉我们你的需求，小蜜桃团队会尽快与你联系。
      </p>

      {status === "success" ? (
        <div className="mt-6 border border-accent-200 bg-accent-50 rounded-lg p-6 text-center">
          <span className="w-12 h-12 rounded-full bg-accent-500 text-background-50 flex items-center justify-center mx-auto">
            <i className="ri-check-line text-2xl"></i>
          </span>
          <p className="mt-4 font-medium text-foreground-900">提交成功，谢谢你！</p>
          <p className="mt-1 text-sm text-foreground-600">
            我们已收到你的信息，会在 1-2 个工作日内与你联系。
          </p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-5 inline-flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 border border-background-200 rounded-md px-4 py-2 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
            再提交一条
          </button>
        </div>
      ) : (
        <form data-readdy-form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="brand-name" className="block text-sm text-foreground-700 mb-1.5">
                称呼 <span className="text-primary-500">*</span>
              </label>
              <input
                id="brand-name"
                name="name"
                type="text"
                required
                placeholder="怎么称呼你"
                className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
              />
            </div>
            <div>
              <label htmlFor="brand-email" className="block text-sm text-foreground-700 mb-1.5">
                邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                id="brand-email"
                name="email"
                type="email"
                required
                placeholder="name@company.com"
                className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="brand-company" className="block text-sm text-foreground-700 mb-1.5">
                公司 / 机构
              </label>
              <input
                id="brand-company"
                name="company"
                type="text"
                placeholder="选填"
                className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400"
              />
            </div>
            <div>
              <label htmlFor="brand-type" className="block text-sm text-foreground-700 mb-1.5">
                合作类型
              </label>
              <select
                id="brand-type"
                name="cooperation_type"
                defaultValue={cooperationTypes[0]}
                className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 transition-colors cursor-pointer"
              >
                {cooperationTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="brand-message" className="block text-sm text-foreground-700 mb-1.5">
              需求描述 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="brand-message"
              name="message"
              required
              maxLength={500}
              rows={5}
              onChange={(e) => setMessageLength(e.target.value.length)}
              placeholder="简单描述你的合作想法或问题（500 字以内）"
              className="w-full bg-background-100 border border-background-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary-400 transition-colors resize-none placeholder:text-foreground-400"
            />
            <p className="mt-1 text-xs text-foreground-400 text-right">
              {messageLength} / 500
            </p>
          </div>

          <input
            className="form-field-decoy"
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          {status === "error" && formError && (
            <div className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{formError}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            {status === "submitting" ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                提交咨询
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}